import React from "react";
import { EonUiIcon } from "@eon-ui/eon-ui-components-react";
import { useTranslation } from "@me8eon/translation";
import { fmt } from "../poData";

// "How much of the total queries got feedback" — a donut split into 👍 / 👎 / no-feedback.
// totalQueries + likes/dislikes come from useDashboardData, so this auto-narrows when a
// category is selected (they're already filtered to the selected set).
type Props = { totalQueries: number; likes: number; dislikes: number; periodDays: number; isCustomPeriod: boolean; tooltip?: string; onClick?: () => void };

const R = 42;
const C = 2 * Math.PI * R;

const FeedbackPie: React.FC<Props> = ({ totalQueries, likes, dislikes, periodDays, isCustomPeriod, tooltip, onClick }) => {
    const t = useTranslation();
    const tp = (key: string) => t(`productDashboard.feedbackPie.${key}`);
    const tc = (key: string) => t(`productDashboard.common.${key}`);
    const total = Math.max(0, totalQueries);
    const feedback = likes + dislikes;
    const noFeedback = Math.max(0, total - feedback);
    const coverage = total ? Math.round((feedback / total) * 100) : 0;
    const clickable = typeof onClick === "function";

    const slices = [
        { key: "likes", label: tp("likes"), value: likes, color: "#1aa055", className: "good" },
        { key: "dislikes", label: tp("dislikes"), value: dislikes, color: "#ea1b0a", className: "bad" },
        { key: "none", label: tp("noFeedback"), value: noFeedback, color: "#94a3b8", className: "" },
    ].filter((s) => s.value > 0);

    let acc = 0;
    return (
        <div
            className={`po-card po-kpi po-feedback-pie${clickable ? " po-kpi--clickable" : ""}`}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={onClick}
            onKeyDown={
                clickable
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onClick!();
                          }
                      }
                    : undefined
            }
            title={tooltip}
        >
            <div className="po-kpi__head">
                <span className="po-kpi__label">{tp("coverage")}</span>
            </div>

            <div className="po-feedback-pie__body">
                <svg
                    className="po-feedback-pie__chart"
                    viewBox="0 0 120 120"
                    width="112"
                    height="112"
                    role="img"
                    aria-label={`${coverage}% of ${fmt(total)} queries received feedback`}
                >
                    {total === 0 ? (
                        <circle cx="60" cy="60" r={R} fill="none" stroke="#e5e7eb" strokeWidth="14" />
                    ) : (
                        slices.map((s) => {
                            const len = (s.value / total) * C;
                            const seg = (
                                <circle
                                    key={s.key}
                                    cx="60"
                                    cy="60"
                                    r={R}
                                    fill="none"
                                    stroke={s.color}
                                    strokeWidth="14"
                                    strokeDasharray={`${len} ${C - len}`}
                                    strokeDashoffset={-acc}
                                    transform="rotate(-90 60 60)"
                                />
                            );
                            acc += len;
                            return seg;
                        })
                    )}
                    <text x="60" y="58" textAnchor="middle" className="po-feedback-pie__pct">{coverage}%</text>
                    <text x="60" y="74" textAnchor="middle" className="po-feedback-pie__sub">{tp("feedback")}</text>
                </svg>

                <ul className="po-feedback-pie__legend">
                    {slices.map((s) => (
                        <li key={s.key} className={`${s.className}`}>
                            {s.key === "likes" && <EonUiIcon size="small" name="thumb_up" className="po-thumb po-thumb--up" />}
                            {s.key === "dislikes" && <EonUiIcon size="small" name="thumb_up" className="po-thumb po-thumb--down" />}
                            <span>{s.label}</span>
                            <b>{fmt(s.value)}</b>
                        </li>
                    ))}
                    {total === 0 && <li className="po-feedback-pie__empty">{tp("noData")}</li>}
                </ul>
            </div>

            <div className="po-kpi__footer">
                <div className="po-kpi__meta po-kpi__meta--no-margin">{tc(isCustomPeriod ? "for" : "last")} {periodDays} {tc(periodDays === 1 ? "day" : "days")}</div>
                {clickable && <span className="po-link po-link--cue">{tp("viewFeedback")}</span>}
            </div>
        </div>
    );
};

export default FeedbackPie;
